/**
 * Turkish Route Redirect Script
 * Redirects Turkish locale routes from Shopify to Next.js app
 * Handles products and other pages
 *
 * NEW: Auto-detects Turkish visitors and redirects them to Turkish locale
 *
 * Local Testing:
 * - Shopify: http://localhost:9292
 * - Next.js: http://localhost:3000
 */

(function () {
  // Configuration - Update these for different environments
  const isLocal =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  const NEXT_APP_DOMAIN = isLocal
    ? 'http://localhost:3000'
    : 'https://tr.marvels.tennis';

  // Patterns to handle different Turkish URL structures
  const TR_PATTERNS = [
    /^\/tr-tr\//, // /tr-tr/* (Shopify's preferred format)
    /^\/en-tr\//, // /en-tr/* (legacy)
  ];

  // Turkey detection configuration
  const TURKEY_COUNTRY_CODE = 'TR';

  // PostHog integration (if available)
  function isPostHogAvailable() {
    return typeof window !== 'undefined' && window.posthog;
  }

  // Enhanced Turkish route detection including subdomain check
  function isTurkishRoute() {
    const path = window.location.pathname;
    const hostname = window.location.hostname;

    // Check for Turkish subdomain
    if (hostname === 'tr.marvels.tennis') {
      return true;
    }

    // Check for Turkish path patterns
    return TR_PATTERNS.some((pattern) => pattern.test(path));
  }

  // Enhanced browser language detection
  function isTurkishLanguage() {
    try {
      const languages = navigator.languages || [navigator.language];
      return languages.some(
        (lang) =>
          lang.toLowerCase().startsWith('tr') ||
          lang.toLowerCase().includes('turkey') ||
          lang.toLowerCase().includes('türkiye'),
      );
    } catch (error) {
      return false;
    }
  }

  // Check if user is from Turkey using multiple detection methods
  function isTurkishVisitor() {
    try {
      // CRITICAL: Check if user explicitly selected a non-Turkish country
      // This should override any automatic detection
      const userExplicitlySelectedNonTurkish =
        sessionStorage.getItem('userSelectedNonTurkish') === 'true';
      if (userExplicitlySelectedNonTurkish) {
        return { isTurkish: false, method: 'user_explicit_choice' };
      }

      // Method 1: Check if already on Turkish route (fastest)
      const isTurkishRouteResult = isTurkishRoute();
      if (isTurkishRouteResult) {
        return { isTurkish: true, method: 'route_pattern' };
      }

      // Method 2: Check Shopify's detected country (most reliable)
      // This is available in the DOM via Shopify's localization object
      const countryElement = document.querySelector(
        'input[name="country_code"]',
      );
      const countryValue = countryElement?.value;
      if (countryElement && countryValue === TURKEY_COUNTRY_CODE) {
        return { isTurkish: true, method: 'shopify_country' };
      }

      // Method 3: Enhanced language detection
      const isTurkishLanguageResult = isTurkishLanguage();
      if (isTurkishLanguageResult) {
        return { isTurkish: true, method: 'browser_language' };
      }

      // Method 4: Check if user has previously selected Turkey (session storage)
      const previousCountry = sessionStorage.getItem('selectedCountry');
      if (previousCountry === TURKEY_COUNTRY_CODE) {
        return { isTurkish: true, method: 'session_storage' };
      }

      return { isTurkish: false, method: 'none' };
    } catch (error) {
      // console.error('❌ isTurkishVisitor - Error:', error);
      // If any detection method fails, default to false
      return { isTurkish: false, method: 'error' };
    }
  }

  // Get the appropriate Turkish locale path
  function getTurkishLocalePath(currentPath) {
    // Check if path already contains tr-tr to prevent double paths
    if (currentPath.includes('/tr-tr/')) {
      return currentPath; // Return as-is to prevent double paths
    }

    // Check if path already starts with tr-tr (exact match)
    if (currentPath.startsWith('/tr-tr')) {
      return currentPath; // Return as-is to prevent double paths
    }

    // Remove any existing locale prefixes
    const cleanPath = currentPath.replace(/^\/(tr|en-tr|tr-tr)\//, '/');

    // Return the Turkish locale path (use tr-tr for main site)
    const result = `/tr-tr${cleanPath}`;
    return result;
  }

  // Track redirect analytics using PostHog (optimized)
  function trackRedirectAnalytics(
    detectionMethod,
    sourcePath,
    targetPath,
    utmParams = {},
  ) {
    try {
      // Update session tracking
      const previousRedirects = parseInt(
        sessionStorage.getItem('redirectCount') || '0',
      );
      sessionStorage.setItem('hasVisited', 'true');
      sessionStorage.setItem(
        'redirectCount',
        (previousRedirects + 1).toString(),
      );
      sessionStorage.setItem('lastRedirectMethod', detectionMethod);

      // Use PostHog if available (much more efficient)
      if (isPostHogAvailable()) {
        window.posthog.capture('turkey_auto_redirect', {
          source_path: sourcePath,
          target_path: targetPath,
          detection_method: detectionMethod,
          detection_confidence: getDetectionConfidence(detectionMethod),
          utm_source: utmParams.utm_source,
          utm_medium: utmParams.utm_medium,
          utm_campaign: utmParams.utm_campaign,
          utm_content: utmParams.utm_content,
          utm_term: utmParams.utm_term,
          referrer: document.referrer,
          is_new_session: !sessionStorage.getItem('hasVisited'),
          previous_redirects: previousRedirects,
          user_language: navigator.language,
          user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
      } else {
        // Fallback: minimal console logging for development
        // console.debug('Turkey redirect:', {
        //   method: detectionMethod,
        //   from: sourcePath,
        //   to: targetPath,
        //   utm: utmParams,
        // });
      }
    } catch (error) {
      // Silently fail - don't break user experience
      // console.debug('Analytics tracking failed:', error);
    }
  }

  // Get detection confidence score
  function getDetectionConfidence(method) {
    const confidence = {
      shopify_country: 0.95, // Most reliable
      browser_language: 0.7, // Good indicator
      session_storage: 0.9, // User previously selected
      route_pattern: 1.0, // Already on Turkish route
      cloudflare_handled: 1.0, // Cloudflare Worker handled it
    };
    return confidence[method] || 0.5;
  }

  // Removed sendAnalytics - now using PostHog directly

  // Extract UTM parameters from URL
  function getUtmParams() {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      utm_source: urlParams.get('utm_source'),
      utm_medium: urlParams.get('utm_medium'),
      utm_campaign: urlParams.get('utm_campaign'),
      utm_content: urlParams.get('utm_content'),
      utm_term: urlParams.get('utm_term'),
    };
  }

  // NEW: Handle Turkish locale page redirects to Turkish subdomain (products, cart, etc.)
  async function handleTurkishLocalePageRedirect() {
    try {
      const currentPath = window.location.pathname;
      const currentHostname = window.location.hostname;

      // Debug logging for troubleshooting
      // console.debug('🔍 Turkish Locale Redirect Check:', {
      //   currentPath,
      //   currentHostname,
      //   isLocal,
      // });

      // Skip if already on Turkish subdomain
      if (
        currentHostname === 'tr.marvels.tennis' ||
        currentHostname === 'localhost:3000'
      ) {
        // console.debug('⏭️ Skipping - already on Turkish subdomain');
        return;
      }

      // Check if we're on a Turkish locale page (products, cart, etc.)
      const turkishLocalePatterns = [
        /^\/tr-tr\/products\//,
        /^\/tr\/products\//,
        /^\/en-tr\/products\//,
        /^\/tr-tr\/cart/,
        /^\/tr\/cart/,
        /^\/en-tr\/cart/,
      ];

      const isTurkishLocalePage = turkishLocalePatterns.some((pattern) =>
        pattern.test(currentPath),
      );

      // console.debug('🔍 Turkish locale page check:', {
      //   isTurkishLocalePage,
      //   patterns: turkishLocalePatterns.map((p) => p.toString()),
      //   currentPath,
      // });

      if (isTurkishLocalePage) {
        // Extract the page path by removing Turkish locale prefixes
        let pagePath = currentPath;

        // Remove Turkish locale prefixes to get clean page path
        // Handle all Turkish locale patterns: /tr-tr/, /tr/, /en-tr/
        pagePath = pagePath.replace(/^\/(tr-tr|tr|en-tr)\//, '/');

        // Ensure it starts with the correct format
        if (pagePath.startsWith('/products/') || pagePath.startsWith('/cart')) {
          // Remove leading slash to match expected format
          pagePath = pagePath.substring(1);
        }

        // console.debug('🔍 Page path extraction:', {
        //   originalPath: currentPath,
        //   extractedPath: pagePath,
        // });

        // Ensure it starts with products/ or cart (with or without trailing slash)
        if (pagePath.startsWith('products/') || pagePath.startsWith('cart')) {
          // Build the Turkish subdomain URL using the actual domain
          const turkishSubdomainUrl = isLocal
            ? `http://localhost:3000/${pagePath}`
            : `https://tr.marvels.tennis/${pagePath}`;
          const currentSearch = window.location.search;
          let finalUrl = turkishSubdomainUrl + currentSearch;

          // If this is a cart page, try to transfer cart data
          if (pagePath.startsWith('cart')) {
            try {
              // Use the cart transfer utility if available
              if (window.turkishCartTransfer) {
                // console.debug(
                //   '🛒 Attempting cart transfer for Turkish locale redirect',
                // );
                const transferUrl =
                  await window.turkishCartTransfer.generateTransferUrl(
                    turkishSubdomainUrl,
                  );
                if (transferUrl && transferUrl !== turkishSubdomainUrl) {
                  finalUrl = transferUrl;
                  // console.debug('🛒 Cart transfer successful:', {
                  //   originalUrl: turkishSubdomainUrl,
                  //   transferUrl: finalUrl,
                  // });
                }
              }
            } catch (error) {
              // console.debug(
              //   '🛒 Cart transfer failed, proceeding with normal redirect:',
              //   error,
              // );
            }
          }

          // console.debug('🚀 Redirecting to Turkish subdomain:', {
          //   from: currentPath,
          //   to: finalUrl,
          //   pagePath,
          //   search: currentSearch,
          //   hasCartData: finalUrl.includes('cart_data'),
          // });

          // Track analytics for Turkish locale page redirect
          if (isPostHogAvailable()) {
            window.posthog.capture('turkish_locale_page_redirect', {
              source_path: currentPath,
              target_path: pagePath,
              source_hostname: currentHostname,
              target_hostname: isLocal ? 'localhost:3000' : 'tr.marvels.tennis',
              page_type: pagePath.startsWith('products/') ? 'product' : 'cart',
              cart_transferred: finalUrl.includes('cart_data'),
              utm_params: getUtmParams(),
              referrer: document.referrer,
            });
          }

          // Redirect to Turkish subdomain
          window.location.replace(finalUrl);
        } else {
          // console.debug('❌ Page path validation failed:', {
          //   pagePath,
          //   startsWithProducts: pagePath.startsWith('products/'),
          //   startsWithCart: pagePath.startsWith('cart'),
          // });
        }
      } else {
        // console.debug('⏭️ Not a Turkish locale page');
      }
    } catch (error) {
      // console.error('❌ handleTurkishLocalePageRedirect - Error:', error);
    }
  }

  // Extract the redirect logic into a separate function
  function performRedirect() {
    try {
      // Check if user is in the middle of switching countries
      const wasCountrySwitching =
        sessionStorage.getItem('countrySwitching') === 'true';

      // If user is switching countries, don't interfere with the redirect
      if (wasCountrySwitching) {
        return;
      }

      // Get current path again in case it changed
      const currentPath = window.location.pathname;

      // Extract the path parts
      const pathParts = currentPath
        .split('/')
        .filter((part) => part.length > 0);

      // Determine the locale prefix and remaining path
      let localePrefix = '';
      let remainingPath = '';

      if (pathParts[0] === 'tr' && pathParts.length > 1) {
        // /tr/something
        localePrefix = 'tr';
        remainingPath = pathParts.slice(1).join('/');
      } else if (pathParts[0] === 'en-tr' && pathParts.length > 1) {
        // /en-tr/something
        localePrefix = 'en-tr';
        remainingPath = pathParts.slice(1).join('/');
      } else if (pathParts[0] === 'tr-tr' && pathParts.length > 1) {
        // /tr-tr/something
        localePrefix = 'tr-tr';
        remainingPath = pathParts.slice(1).join('/');
      }

      if (remainingPath) {
        // Check if path already contains tr-tr to prevent double paths
        if (remainingPath.includes('tr-tr/')) {
          return;
        }

        // Only redirect product pages to Next.js subdomain
        if (remainingPath.startsWith('products/')) {
          const nextAppUrl = `${NEXT_APP_DOMAIN}/${remainingPath}`;
          const currentSearch = window.location.search;
          const finalUrl = nextAppUrl + currentSearch;

          // Redirect to Next.js app
          window.location.replace(finalUrl);
        } else {
          // For non-product pages, check if we need to redirect to clean up URLs
          // Only redirect if the current URL doesn't already match the clean format
          const currentDomain = window.location.origin;
          const cleanPath = `/tr-tr/${remainingPath}`;
          const currentSearch = window.location.search;
          const finalUrl = currentDomain + cleanPath + currentSearch;

          // Check if we're already at the clean URL to avoid circular redirects
          const currentUrl = window.location.href;
          const isAlreadyClean =
            currentUrl === finalUrl ||
            currentUrl.replace(/\/$/, '') === finalUrl.replace(/\/$/, '');

          if (!isAlreadyClean && !isLocal) {
            // Only redirect in production to clean up URLs
            // In local development, let them stay as they are to avoid breaking local testing
            window.location.replace(finalUrl);
          }
        }
      }
    } catch (error) {
      // console.error('❌ performRedirect - Error:', error);
    }
  }

  // Handle Turkey-based auto-redirect for non-Turkish visitors
  function handleTurkeyAutoRedirect() {
    try {
      // Skip if already on Turkish route
      if (isTurkishRoute()) {
        return;
      }

      // Skip if user was switching countries (set by the switcher)
      const wasCountrySwitching =
        sessionStorage.getItem('countrySwitching') === 'true';
      if (wasCountrySwitching) {
        return;
      }

      // Skip if user came from Turkish subdomain (Next.js app)
      const cameFromTurkishSubdomain =
        sessionStorage.getItem('cameFromTurkishSubdomain') === 'true';
      if (cameFromTurkishSubdomain) {
        // Clear the flag after processing
        sessionStorage.removeItem('cameFromTurkishSubdomain');
        return;
      }

      // Skip if user was redirected from Turkish subdomain recently
      const redirectedFromTurkishSubdomain =
        sessionStorage.getItem('redirectedFromTurkishSubdomain') === 'true';
      if (redirectedFromTurkishSubdomain) {
        return;
      }

      // Check if user is from Turkey
      const detection = isTurkishVisitor();

      if (detection.isTurkish) {
        const currentPath = window.location.pathname;

        // CRITICAL FIX: Check if user is already on a Turkish route
        // Don't redirect if they're already on /tr-tr or similar Turkish paths
        if (
          currentPath.startsWith('/tr-tr') ||
          currentPath.startsWith('/tr/') ||
          currentPath.startsWith('/en-tr/')
        ) {
          return;
        }

        const turkishPath = getTurkishLocalePath(currentPath);
        const currentSearch = window.location.search;
        const finalUrl = window.location.origin + turkishPath + currentSearch;

        // Track analytics before redirect
        const utmParams = getUtmParams();
        trackRedirectAnalytics(
          detection.method,
          currentPath,
          turkishPath,
          utmParams,
        );

        // Store the redirect in session to avoid loops
        sessionStorage.setItem('turkeyAutoRedirect', 'true');
        // Mark that client-side handled the redirect (not Cloudflare)
        sessionStorage.setItem('clientSideRedirect', 'true');

        // Redirect to Turkish locale
        window.location.replace(finalUrl);
      }
    } catch (error) {
      // console.error('❌ handleTurkeyAutoRedirect - Error:', error);
    }
  }

  // Auto-set country to TR if on Turkish routes
  function setCountryToTurkey() {
    try {
      const countryElement = document.querySelector(
        'input[name="country_code"]',
      );
      if (countryElement && countryElement.value !== 'TR') {
        const previousCountry = countryElement.value;

        // Update the country selection
        countryElement.value = 'TR';

        // Update visual elements (flag, currency, country name)
        updateCountryDisplay('TR');

        // Trigger Shopify's localization form submission
        const localizationForm = document.querySelector(
          'form[action*="localization"]',
        );
        if (localizationForm) {
          // Submit the form to trigger Shopify's server-side localization
          localizationForm.submit();
          return; // Exit early since form submission will reload the page
        }

        // Fallback: Update session storage if form submission fails
        sessionStorage.setItem('selectedCountry', 'TR');
        sessionStorage.setItem('countryAutoSet', 'true');

        // Track analytics for auto country setting
        if (isPostHogAvailable()) {
          window.posthog.capture('country_auto_set', {
            country: 'TR',
            method: 'turkish_route_detection',
            route: window.location.pathname,
            previous_country: previousCountry,
          });
        }

        // console.debug('Country automatically set to TR for Turkish route');
      }
    } catch (error) {
      // console.debug('Failed to auto-set country:', error);
    }
  }

  // Clear country switching flag after successful switch
  function clearCountrySwitchingFlag() {
    try {
      // Clear the flag after a short delay to allow the form submission to complete
      setTimeout(() => {
        sessionStorage.removeItem('countrySwitching');
      }, 1000);
    } catch (error) {}
  }

  // Force Turkish display for Turkish routes (triggers Shopify localization)
  function forceTurkishDisplay() {
    try {
      const countryElement = document.querySelector(
        'input[name="country_code"]',
      );

      if (countryElement && countryElement.value !== 'TR') {
        // Update the hidden form input
        countryElement.value = 'TR';

        // Find and submit the localization form to trigger Shopify's server-side update
        const localizationForm = document.querySelector(
          'form[action*="localization"]',
        );
        if (localizationForm) {
          // Submit the form to trigger Shopify's localization system
          localizationForm.submit();
          return; // Exit early since form submission will reload the page
        }

        // Fallback: Update session storage if form submission fails
        sessionStorage.setItem('selectedCountry', 'TR');
        sessionStorage.setItem('countryAutoSet', 'true');

        // console.debug('Forced Turkish localization for Turkish route');
      }
    } catch (error) {
      // console.debug('Failed to force Turkish display:', error);
    }
  }

  const INLINE_FLAG_CODES = ['US', 'AU', 'CA', 'FR', 'GB', 'TR', 'EU'];

  function getFlagAltText(countryCode, isEUCountry) {
    if (isEUCountry || countryCode === 'EU') return 'European Union';
    if (countryCode === 'TR') return 'Türkiye';
    return null;
  }

  function updateFlagInContainer(container, displayCountryCode, altText) {
    if (!container) return;

    const code = displayCountryCode.toUpperCase();
    const svg = container.querySelector('svg.flag-icon');
    const img = container.querySelector('img');
    const alt = altText || getFlagAltText(code, code === 'EU') || '';

    if (INLINE_FLAG_CODES.includes(code) && svg) {
      const useEl = svg.querySelector('use');
      if (useEl) {
        useEl.setAttribute('href', `#flag-${code.toLowerCase()}`);
      }
      svg.setAttribute('data-flag', code);
      if (alt) svg.setAttribute('aria-label', alt);
      svg.style.display = '';
      if (img) img.style.display = 'none';
    }
  }

  // Update visual country display elements
  function updateCountryDisplay(countryCode) {
    try {
      // Handle EU countries - map to EU flag and Euro symbol
      const isEUCountry = [
        'DE',
        'FR',
        'ES',
        'IT',
        'BE',
        'NL',
        'AT',
        'DK',
        'SE',
        'NO',
        'CH',
      ].includes(countryCode);
      const displayCountryCode = isEUCountry ? 'EU' : countryCode;
      const flagAlt = getFlagAltText(countryCode, isEUCountry);

      // Update header flag (top button) explicitly
      try {
        const headerFlagContainer = document.querySelector(
          '.button-localization .localization-flags',
        );
        updateFlagInContainer(
          headerFlagContainer,
          displayCountryCode,
          flagAlt,
        );
      } catch {}

      // Update flags in the dropdown list for the active country
      const flagContainers = document.querySelectorAll('.localization-flags');
      flagContainers.forEach((container) => {
        const parentElement =
          container.closest('[data-country]') ||
          container.closest('button') ||
          container.closest('.country-option') ||
          container.closest('.localization-item');

        if (!parentElement) return;

        const currentCountry =
          parentElement.getAttribute('data-country') ||
          parentElement.getAttribute('data-value') ||
          parentElement
            .querySelector('[data-country]')
            ?.getAttribute('data-country');

        if (
          currentCountry === countryCode ||
          (isEUCountry && currentCountry === 'EU')
        ) {
          updateFlagInContainer(container, displayCountryCode, flagAlt);
        }
      });

      // Update country name in buttons
      const countryButtons = document.querySelectorAll('[data-value]');
      countryButtons.forEach((button) => {
        if (
          button.getAttribute('data-value') === countryCode ||
          (isEUCountry && button.getAttribute('data-value') === 'EU')
        ) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });

      // Update currency symbol in header
      try {
        const symbolByCountry = {
          TR: '₺',
          US: '$',
          GB: '£',
          CA: '$',
          AU: '$',
        };
        const headerSymbol = document.querySelector(
          '.button-localization .color-black-white',
        );
        if (headerSymbol) {
          headerSymbol.textContent = isEUCountry
            ? '€'
            : symbolByCountry[countryCode] || headerSymbol.textContent;
        }
      } catch {}

      // Trigger any custom country change events
      const customEvent = new CustomEvent('countryChanged', {
        detail: {
          countryCode: countryCode,
          displayCountryCode: displayCountryCode,
        },
      });
      document.dispatchEvent(customEvent);
    } catch (error) {
      // console.debug('Failed to update country display:', error);
    }
  }

  // NEW: On page load, read selection from sessionStorage, cookies or query
  try {
    const fromQuery = new URLSearchParams(window.location.search);
    const qCountry = fromQuery.get('sc');
    const qDisplay = fromQuery.get('scd');
    const qCurrency = fromQuery.get('scu');
    const qSymbol = fromQuery.get('scs');

    const cookieMap = Object.fromEntries(
      document.cookie
        .split('; ')
        .filter(Boolean)
        .map((kv) => kv.split('=')),
    );

    // Also read Shopify-rendered current country from localization form
    const countryInputEl = document.querySelector('input[name="country_code"]');
    const shopifyCountry = countryInputEl?.value || '';

    // PRIORITY: query > shopifyCountry > cookies > sessionStorage
    const persistedCountry =
      qCountry ||
      shopifyCountry ||
      cookieMap['selectedCountry'] ||
      sessionStorage.getItem('selectedCountry');
    const persistedDisplay =
      qDisplay ||
      cookieMap['selectedCountryDisplay'] ||
      sessionStorage.getItem('selectedCountryDisplay');
    const persistedCurrency =
      qCurrency ||
      cookieMap['selectedCurrency'] ||
      sessionStorage.getItem('selectedCurrency');
    const persistedCurrencySymbol =
      qSymbol ||
      cookieMap['selectedCurrencySymbol'] ||
      sessionStorage.getItem('selectedCurrencySymbol');

    if (persistedCountry && persistedCountry !== 'TR') {
      updateCountryDisplay(persistedCountry);

      // Persist selection into sessionStorage for future navigations
      try {
        const toStoreCountry = qCountry || shopifyCountry || persistedCountry;
        if (toStoreCountry)
          sessionStorage.setItem('selectedCountry', toStoreCountry);
        if (qDisplay)
          sessionStorage.setItem('selectedCountryDisplay', qDisplay);
        else if (persistedDisplay)
          sessionStorage.setItem('selectedCountryDisplay', persistedDisplay);
        else sessionStorage.removeItem('selectedCountryDisplay');
        if (qCurrency) sessionStorage.setItem('selectedCurrency', qCurrency);
        else if (persistedCurrency)
          sessionStorage.setItem('selectedCurrency', persistedCurrency);
        else sessionStorage.removeItem('selectedCurrency');
        if (qSymbol) sessionStorage.setItem('selectedCurrencySymbol', qSymbol);
        else if (persistedCurrencySymbol)
          sessionStorage.setItem(
            'selectedCurrencySymbol',
            persistedCurrencySymbol,
          );
        else sessionStorage.removeItem('selectedCurrencySymbol');
      } catch {}

      // Clean up URL by removing query parameters after reading them
      if (qCountry || qDisplay || qCurrency || qSymbol) {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('sc');
        cleanUrl.searchParams.delete('scd');
        cleanUrl.searchParams.delete('scu');
        cleanUrl.searchParams.delete('scs');

        // Only update if there are actually query params to remove
        if (cleanUrl.toString() !== window.location.href) {
          window.history.replaceState({}, '', cleanUrl.toString());
        }
      }

      // One-time Shopify localization form sync to persist selection server-side
      try {
        let recentlySubmitted =
          sessionStorage.getItem('formRecentlySubmitted') === 'true';
        const countryInput = document.querySelector(
          'input[name="country_code"]',
        );
        const currentShopifyCountry = countryInput?.value;
        // If selection arrived via query params, allow sync by resetting the guard
        const selectionArrivedViaQuery = Boolean(qCountry);
        if (selectionArrivedViaQuery) {
          sessionStorage.removeItem('formRecentlySubmitted');
          recentlySubmitted = false;
          // Normalize session storage to reflect the query selection
          try {
            sessionStorage.setItem('selectedCountry', persistedCountry);
          } catch {}
        }

        const needsSync =
          countryInput &&
          currentShopifyCountry &&
          currentShopifyCountry !== persistedCountry;

        if (!recentlySubmitted && needsSync) {
          countryInput.value = persistedCountry;
          const locForm = countryInput.closest('form');
          if (locForm) {
            sessionStorage.setItem('formRecentlySubmitted', 'true');
            setTimeout(
              () => sessionStorage.removeItem('formRecentlySubmitted'),
              5000,
            );
            locForm.submit();
          }
        }
      } catch (e) {
        // console.debug('Localization sync - skipped:', e);
      }
    }
  } catch (e) {
    // console.debug('Startup - No persisted selection found');
  }

  // Handle Turkish locale page redirects to Turkish subdomain (highest priority)
  // This runs independently of any session storage flags since it's a direct URL pattern match
  handleTurkishLocalePageRedirect().catch((error) => {
    // console.error('❌ Turkish locale page redirect failed:', error);
  });

  // Add a delay to allow country switcher to work first
  setTimeout(() => {
    // Check if redirect script is disabled (set by country switcher)
    const scriptDisabled =
      sessionStorage.getItem('redirectScriptDisabled') === 'true';
    if (scriptDisabled) {
      return; // Exit completely
    }

    // Check session storage flags
    const cameFromTurkishSubdomain =
      sessionStorage.getItem('cameFromTurkishSubdomain') === 'true';
    const redirectedFromTurkishSubdomain =
      sessionStorage.getItem('redirectedFromTurkishSubdomain') === 'true';
    const wasCountrySwitching =
      sessionStorage.getItem('countrySwitching') === 'true';
    const formRecentlySubmitted =
      sessionStorage.getItem('localizationFormSubmitted') === 'true';

    // Handle /tr -> /tr-tr/ redirect first (before other logic)
    const currentPath = window.location.pathname;
    if (
      (currentPath === '/tr' || currentPath.startsWith('/tr/')) &&
      !currentPath.startsWith('/tr-tr/')
    ) {
      const cleanPath = currentPath.replace(/^\/tr(\/|$)/, '/tr-tr$1');
      const newUrl =
        window.location.origin + cleanPath + window.location.search;
      window.location.replace(newUrl);
      return; // Exit early to prevent other redirects
    }

    // Check if current URL matches any Turkish pattern
    const isTurkishRouteResult = isTurkishRoute();

    if (isTurkishRouteResult) {
      try {
        // If user is switching countries or form was submitted, don't interfere at all
        if (wasCountrySwitching || formRecentlySubmitted) {
          // Clear the flags after a delay
          clearCountrySwitchingFlag();
          setTimeout(() => {
            sessionStorage.removeItem('localizationFormSubmitted');
          }, 1000);
          return; // Exit early to prevent any interference
        }

        // Check if user came from Turkish subdomain
        if (cameFromTurkishSubdomain) {
          sessionStorage.removeItem('cameFromTurkishSubdomain');
          return;
        }

        // Check if user was redirected from Turkish subdomain
        if (redirectedFromTurkishSubdomain) {
          return;
        }

        // CRITICAL FIX: Check if user is trying to switch to a non-Turkish country
        // This prevents the script from forcing Turkish display when user wants to go to US/EU/etc
        const countryElement = document.querySelector(
          'input[name="country_code"]',
        );
        const currentCountry = countryElement?.value;

        // If user is on a Turkish route but has selected a non-Turkish country,
        // they want to switch away from Turkish - don't force Turkish display
        if (currentCountry && currentCountry !== 'TR') {
          // Don't force Turkish display or perform redirects
          return;
        }

        // Only force Turkish display if the country is not already set to TR
        const isAlreadyTurkish =
          countryElement && countryElement.value === 'TR';

        // Only force Turkish display if not already Turkish
        if (!isAlreadyTurkish) {
          // Force country switcher to display TR flag and Lira for Turkish routes
          forceTurkishDisplay();
        }

        // Continue with the original redirect logic (but only for non-switching scenarios)
        performRedirect();
      } catch (error) {
        // TODO: Implement Sentry logging
        // console.error(
        //   '❌ TR Redirect Script - Error in Turkish route processing:',
        //   error,
        // );
      }
    } else {
      // CRITICAL: Check if user explicitly selected non-Turkish country
      const userSelectedNonTurkish =
        sessionStorage.getItem('userSelectedNonTurkish') === 'true';

      // Also check if we came from Turkish subdomain (indicates user made a choice)
      const cameFromTurkishSubdomain =
        sessionStorage.getItem('cameFromTurkishSubdomain') === 'true';

      // Also check referrer to see if user came from Turkish subdomain
      const referrer = document.referrer;
      const cameFromTurkishSubdomainReferrer =
        referrer.includes('tr.marvels.tennis') ||
        referrer.includes('localhost:3000');

      if (
        userSelectedNonTurkish ||
        cameFromTurkishSubdomain ||
        cameFromTurkishSubdomainReferrer
      ) {
        return;
      }

      // If not on Turkish route, handle auto-redirect for Turkish visitors
      handleTurkeyAutoRedirect();
    }
  }, 1000); // Increased delay to ensure country switcher has time to set flags
})();
